import { Error } from 'utils/alerts'

/**
 * Handle API errors with detailed error messages and authentication guidance
 * @param {Error} error - The error object from the API call
 * @param {Function} handleDiscard - Function to navigate back to list
 */
export const handleApiError = (error, handleDiscard) => {
    console.error('Error creating invoice:', error)
    console.error('Error response:', error.response?.data)
    console.error('Error status:', error.response?.status)

    // Show detailed error message with authentication guidance
    if (error.response?.data) {
        const errorData = error.response.data

        // Check for invoice not found error
        if (errorData.error_type === 'invoice_not_found') {
            Error(`${errorData.message} The invoice may have been deleted in Xero or may not exist in this Xero organization.`)
            // Redirect back to list after showing error
            setTimeout(() => {
                handleDiscard()
            }, 3000)
            return
        }
        // Check for validation errors from our enhanced backend response
        else if (errorData.validation_errors && errorData.validation_errors.length > 0) {
            // Display each validation error in a separate notification
            errorData.validation_errors.forEach(errorMsg => {
                Error(`Validation Error: ${errorMsg}`)
            })
        } else if (errorData.details && errorData.details.Elements) {
            // Try to extract validation errors directly from Xero response structure
            const validationErrors = []
            errorData.details.Elements.forEach(element => {
                if (element.ValidationErrors) {
                    element.ValidationErrors.forEach(error => {
                        if (error.Message) {
                            validationErrors.push(error.Message)
                        }
                    })
                }
            })

            if (validationErrors.length > 0) {
                validationErrors.forEach(errorMsg => {
                    Error(`Xero Validation: ${errorMsg}`)
                })
            } else {
                // If we couldn't extract specific validation errors, show the general message
                Error(`Xero API Error: ${errorData.message}`)
            }
        } else if (typeof errorData === 'string') {
            if (errorData.includes('AuthenticationUnsuccessful') || errorData.includes('Forbidden')) {
                Error(
                    'Xero authentication failed. The tokens may have incorrect permissions or have expired. Please go to Company Management > Xero Integration and re-authorize the configuration by clicking "Test Connection".'
                )
            } else {
                Error(`API Error: ${errorData}`)
            }
        } else if (errorData.message) {
            if (errorData.message.includes('AuthenticationUnsuccessful') || errorData.message.includes('Forbidden')) {
                Error(
                    'Xero authentication failed. The tokens may have incorrect permissions or have expired. Please go to Company Management > Xero Integration and re-authorize the configuration by clicking "Test Connection".'
                )
            } else {
                Error(`API Error: ${errorData.message}`)
            }
        } else if (errorData.error) {
            Error(`API Error: ${errorData.error}`)
        } else {
            Error(`API Error: ${JSON.stringify(errorData)}`)
        }
    } else {
        Error(error.message || 'Failed to create invoice')
    }
}

/**
 * Validate company and configuration before form submission
 * @param {Object} selectedCompany - Selected company object
 * @param {Object} globalCompany - Global company from navbar
 * @param {Object} xeroConfig - Xero configuration object
 * @param {string} id - Invoice ID (for edit mode)
 * @param {Object} recordData - Record data (for edit mode)
 * @returns {Object} Validation result with company and config to use
 */
export const validateSubmissionRequirements = (selectedCompany, globalCompany, xeroConfig, id, recordData) => {
    // Check both selectedCompany and globalCompany
    if (!selectedCompany && !globalCompany) {
        Error('Please select a company first.')
        return { isValid: false }
    }

    // Use whichever company is available
    const companyToUse = selectedCompany || globalCompany

    // If we don't have a xeroConfig but we're in edit mode with a configuration_id, try to use that
    const editModeConfigId = id && recordData && recordData.configuration_id

    if (!xeroConfig && !editModeConfigId) {
        const companyName = companyToUse?.name || `ID: ${companyToUse?.id}` || 'the selected company'
        Error(`No Xero configuration found for ${companyName}. Please set up Xero integration for this company first.`)
        return { isValid: false }
    }

    // CRITICAL FIX: Validate company-configuration context in edit mode
    if (id && recordData && editModeConfigId) {
        // Check if the invoice's company matches the current company context
        const invoiceCompanyId = recordData.company_id
        const currentCompanyId = companyToUse.id

        if (invoiceCompanyId && currentCompanyId && invoiceCompanyId !== currentCompanyId) {
            const currentCompanyName = companyToUse?.name || `ID: ${currentCompanyId}` || 'Unknown Company'

            Error(
                `Company context mismatch: This invoice belongs to a different company (ID: ${invoiceCompanyId}) than the currently selected company (ID: ${currentCompanyId}, Name: ${currentCompanyName}). ` +
                    `Please switch to the correct company or select the appropriate invoice for editing.`
            )
            console.error('Company-Configuration Mismatch Details:', {
                invoiceId: id,
                invoiceCompanyId,
                currentCompanyId,
                configurationId: editModeConfigId,
                currentCompanyName,
                companyToUse,
                globalCompany,
                selectedCompany,
            })
            return { isValid: false }
        }

        // If we have a full xeroConfig, validate it belongs to the current company
        if (xeroConfig && xeroConfig.company && xeroConfig.company.id !== currentCompanyId) {
            const configCompanyName = xeroConfig.company.name || `ID: ${xeroConfig.company.id}`
            const currentCompanyName = companyToUse?.name || `ID: ${currentCompanyId}` || 'Unknown Company'

            Error(
                `Configuration mismatch: The selected Xero configuration belongs to company "${configCompanyName}" ` +
                    `but you're trying to edit an invoice for company "${currentCompanyName}". ` +
                    `Please select the correct configuration or switch to the appropriate company.`
            )
            console.error('Configuration Company Mismatch:', {
                configCompanyId: xeroConfig.company.id,
                configCompanyName,
                currentCompanyId,
                currentCompanyName,
                companyToUse,
                globalCompany,
                selectedCompany,
            })
            return { isValid: false }
        }

        console.log('Company-Configuration validation passed:', {
            invoiceId: id,
            invoiceCompanyId,
            currentCompanyId,
            configurationId: editModeConfigId,
            companyName: companyToUse.name,
        })
    }

    // Use the xeroConfig from state or create a minimal one from the recordData if in edit mode
    const configToUse = xeroConfig || (editModeConfigId ? { id: editModeConfigId } : null)

    // Only check token validity if we have a full xeroConfig object (not in edit mode with just an ID)
    if (xeroConfig && (!xeroConfig.tenant_id || xeroConfig.is_token_expired)) {
        if (!xeroConfig.tenant_id) {
            Error(
                'Xero configuration is not authorized. Please go to Company Management > Xero Integration and click "Test Connection" or "Authorize" to complete the OAuth flow.'
            )
        } else {
            Error(
                'Xero configuration tokens have expired. Please go to Company Management > Xero Integration and click "Test Connection" to refresh the tokens.'
            )
        }
        return { isValid: false }
    }

    return {
        isValid: true,
        companyToUse,
        configToUse,
    }
}
